import { ready } from "./utils";

/* Toggles a form's inputs' disabled prop, as well as graying-out/ungraying-out the form group */
const toggleFormDisabled = form => {
  // x ^= true flips the boolean x (i.e., xors it with true)
  [...form.querySelectorAll('input:not(.pre-disabled)')].forEach(elt => elt.disabled ^= true);
  // Uses a cancellable promise (h/t Bluebird) to visually gray out each elt *only* on a slow connection
  // This prevents a "flicker" if the connection is fast
  // If the elt is in its base state, create a Promise which says "gray me out in 500ms unless I'm canceled"
  // If the elt has a promise attached to it, cancel the promise and remove the rejected promise
  // If the elt is already grayed out, ungray it out and remove the resolved promise
  const delay = 500;
  [...form.getElementsByTagName('label')].forEach(elt => {
    if (elt.classList.contains('in-progress')) {
      elt.classList.remove('in-progress');
      delete elt.promise;
    }
    else if (elt.promise) {
      elt.promise.cancel();
      delete elt.promise;
    }
    else {
      const promise = new Promise((resolve, reject, onCancel) => {
        const id = setTimeout(resolve, delay);
        onCancel(() => clearTimeout(id));
      });
      elt.promise = promise;
      promise.then(() => elt.classList.add('in-progress'));
    }
  });
};

/* Rails does not play nicely with the FormData API. Therefore manually create FormData and append the inputs */
const gatherFormValues = form => {
  const formData = new FormData(form);
  [...form.querySelectorAll('input[type="radio"], input[type="checkbox"]')].forEach(elt => {
    if (elt.type == "radio" && elt.checked) {
      formData.append(elt.name, elt.value);
    }
    else if (elt.type == "checkbox") {
      let value = elt.dataset.checkedValue
        ? elt.checked
          ? elt.dataset.checkedValue
          : elt.dataset.uncheckedValue
        : elt.checked;
      formData.append(elt.name, value);
    }
  });
  // Add a jwt, if one is present
  const jwt = form.querySelector('#user_jwt');
  if (jwt) {
    formData.append('jwt', jwt.value)
  }
  return formData;
};

/* Updates a user's settings upon clicking one of the radio button or checkboxes */
const updateSettings = async e => {
  const form = e.currentTarget;
  form.removeEventListener('change', updateSettings);
  toggleFormDisabled(form);
  const
    csrfHiddenField = form.querySelector('[name="authenticity_token"]'),
    headers = new Headers({
      'X-CSRF-Token': csrfHiddenField ? csrfHiddenField.value : 'DEFAULT_CSRF', // Provide a fallback for Selenium
      'X-Requested-With': 'XMLHttpRequest'
    }),
    formData = gatherFormValues(form),
    payload = {
      headers: headers,
      credentials: 'same-origin',
      method: form.querySelector('[name=_method]').value.toUpperCase(),
      body: formData
    },
    request = new Request(form.action, payload);
  try {
    const response = await fetch(request);
    if (response.ok) {
      const
        responseBody = await response.json(),
        formControlMainLabel = e.target.parentNode.querySelector('.form-control-maintext');
      // Don't just add "saved", i.e. trigger the animation - first cancel any animation currently happening
      formControlMainLabel.classList.remove('saved');
      void formControlMainLabel.offsetWidth; // Force a reflow to cancel the current animation
      formControlMainLabel.classList.add('saved');
      toggleFormDisabled(form);
      form.addEventListener('change', updateSettings);
    }
    else {
      throw new Error(response.statusText);
    }
  }
  catch (e) {
    console.error(e);
    // Reload the page on error to show a flash
    // This happens so rarely that it's worth not worrying about the bad user experience
    location.reload();
  }
};

/* Slides/unslides the topics section */
const toggleTopicsSection = e => {
  // Prevent this from triggering the radio button
  e.preventDefault();
  // Remove the event listener
  const topicsControlSlider = e.currentTarget;
  topicsControlSlider.removeEventListener('click', toggleTopicsSection);
  // Set up some locals
  const
    topics = document.getElementById('topics'),
    chevron = document.querySelector('.fa-chevron-right'),
    curHeight = topics.scrollHeight,
    animation = newHeight => { // The animation
      topics.style.height = `${newHeight}px`;
      chevron.classList.toggle('rotated');
    };
  // Placeholder for the afterAnimation function. The variable just has to be defined here
  let afterAnimation;
  // Create a function composed of the common post-animation hooks and any optional extras
  // This function can then be set as an event listener for 'transitionend'
  // Intentionally takes an empty function as an optional argument
  const afterAnimationFactory = (extras = () => {}) => {
    return () => {
      topics.removeEventListener('transitionend', afterAnimation);
      extras();
      topicsControlSlider.addEventListener('click', toggleTopicsSection);
    };
  };
  if (topics.dataset.collapsed === 'true') {
    topics.dataset.collapsed = 'false';
    // Set the height to auto after the animation is done
    afterAnimation = afterAnimationFactory(() => topics.style.height = 'auto');
    topics.addEventListener('transitionend', afterAnimation);
    requestAnimationFrame(() => animation(curHeight));
  }
  else {
    topics.dataset.collapsed = 'true'
    topics.style.height = `${curHeight}px`; // Make sure height is not auto
    topics.addEventListener('transitionend', afterAnimationFactory());
    requestAnimationFrame(() => animation(0));
  }
};

/* Set up event listeners */
const settingsHandler = () => {
  const forms = document.querySelectorAll('#privacy-settings-form, .email-settings-form');
  forms.forEach(form => {
    if (!form) return;
    // Make a request to the server upon form changes
    form.addEventListener('change', updateSettings);
  })

  // Make the "View Topics" link toggle the topics section
  const topicsSliderControl = document.querySelector('.topics-slider-control');
  if (topicsSliderControl) {
    topicsSliderControl.addEventListener('click', toggleTopicsSection);

    // Make the "Articles" section trigger the topic slider based on which option is selected
    let selectedArticleOptionId = document.querySelector('[name="user[article_notifications]"]:checked').id;
    const
      topicsCheckBoxId = 'user_article_notifications_topics',
      topicsSlider = document.querySelector('#topics');
    [...document.querySelectorAll('[name="user[article_notifications]"]')].forEach(elt => {
      elt.addEventListener('change', e => {
        const target = e.target;
        // Trigger the slider if a) the new selection is the topics checkbox; or b) the old selection was the topics checkbox
        // The additional state checks help keep the UI in sync if the user tries to break it by clicking around quickly
        if ((target.id == topicsCheckBoxId && topics.dataset.collapsed === 'true') ||
            (selectedArticleOptionId == topicsCheckBoxId && topics.dataset.collapsed === 'false')) {
          topicsSliderControl.click();
        }
        // Update the currently selected option
        selectedArticleOptionId = target.id;
      });
    });
  }

  // Make sure that any "Saved" animation which completes removes the "saved" such that the animation can be re-triggered
  [...document.querySelectorAll('.form-control-maintext')].forEach(
    elt => elt.addEventListener('animationend', e => e.target.classList.remove('saved'))
  );
};

ready(settingsHandler);
